import type { FindingDetailDto } from '@terra/shared-contracts-findings';
import { isEqual } from 'radash';

export type EditableFields = Pick<
  FindingDetailDto,
  | 'title'
  | 'description'
  | 'poc'
  | 'pocBlocks'
  | 'businessImpact'
  | 'mitigationPlan'
  | 'status'
  | 'severity'
  | 'category'
  | 'assignedTo'
  | 'signalId'
  | 'traceThreadId'
  | 'assets'
  | 'isPublished'
  | 'findingUpdateComment'
> & {
  shortDescription: string;
  description: string;
  poc: string;
  businessImpact: string;
  mitigationPlan: string;
  cvss?: FindingDetailDto['cvss'];
  cvss40?: FindingDetailDto['cvss'];
  cvss31?: FindingDetailDto['cvss'];
  severityReason?: string;
  owaspRisk?: FindingDetailDto['owaspRisk'];
};
type EditableFieldKey = keyof EditableFields;
const EDITABLE_FIELD_KEYS = [
  'title',
  'shortDescription',
  'description',
  'poc',
  'pocBlocks',
  'businessImpact',
  'mitigationPlan',
  'status',
  'severity',
  'category',
  'assignedTo',
  'signalId',
  'traceThreadId',
  'assets',
  'isPublished',
  'findingUpdateComment',
  'cvss',
  'cvss40',
  'cvss31',
  'severityReason',
  'owaspRisk',
] as const satisfies readonly EditableFieldKey[];
const NULLABLE_FIELDS = [
  'shortDescription',
  'description',
  'poc',
  'businessImpact',
  'mitigationPlan',
  'signalId',
  'traceThreadId',
  'findingUpdateComment',
] as const satisfies readonly EditableFieldKey[];
const NULLABLE_FIELD_SET = new Set<EditableFieldKey>(NULLABLE_FIELDS);

export const getChangedEditableFieldKeys = ({
  editableFields,
  originalFields,
}: {
  editableFields: EditableFields;
  originalFields: EditableFields;
}): EditableFieldKey[] =>
  EDITABLE_FIELD_KEYS.filter(
    (key) => !isEqual(editableFields[key], originalFields[key]),
  );
export const hasEditableFieldChanges = ({
  editableFields,
  originalFields,
}: {
  editableFields: EditableFields;
  originalFields: EditableFields;
}): boolean =>
  getChangedEditableFieldKeys({ editableFields, originalFields }).length > 0;

export const computeEditableFieldUpdates = ({
  editableFields,
  originalFields,
  owaspRiskEditMode,
}: {
  editableFields: EditableFields;
  originalFields: EditableFields;
  owaspRiskEditMode?: 'editable' | 'deferred';
}): Partial<EditableFields> =>
  getChangedEditableFieldKeys({
    editableFields,
    originalFields,
  }).reduce<Partial<EditableFields>>((acc, key) => {
    if (owaspRiskEditMode === 'deferred' && key === 'owaspRisk') {
      return acc;
    }

    let value = editableFields[key];

    if (NULLABLE_FIELD_SET.has(key) && value === '') {
      value = null;
    }

    if (value !== undefined) {
      (acc as Record<string, unknown>)[key] = value;
    }

    return acc;
  }, {});
